import {useQuery} from "@tanstack/react-query";
import {
    GetTransactionsPaginatedResponse,
    GetTransactionsPaginatedSort,
    SortOrder,
    Token,
    TransactionsApi,
} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {useStatsigClient} from "@statsig/react-bindings";

interface TransactionsPaginatedQueryParams {
    sort?: GetTransactionsPaginatedSort;
    order?: SortOrder;
    limit?: number;
    offset?: number
}

async function fetchTransactionsPaginated(fullToken: Token, userId: string, params: TransactionsPaginatedQueryParams): Promise<GetTransactionsPaginatedResponse> {
    return await new TransactionsApi(getConfig(fullToken)).getTransactionsPaginated({
        ...params,
        id: userId,
    });
}

function transactionsPaginatedQuery(fullToken: Token, userId: string, params: TransactionsPaginatedQueryParams, disabled?: boolean) {
    const { client: statsigClient } = useStatsigClient();
    const dpayStatus = statsigClient.checkGate(import.meta.env.VITE_TOKEN_STATSIG_STATUS);

    return {
        queryKey: [
            "transactions-paginated",
            fullToken?.id ?? "unauthenticated",
            userId ?? "no-users-id",
            params,
        ],
        queryFn: () => fetchTransactionsPaginated(fullToken, userId, params),
        enabled: Boolean(fullToken && userId && dpayStatus) && !disabled,
        keepPreviousData: true,
        staleTime: 30 * 1000, // Consider data fresh for 30 seconds
        gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
        refetchOnWindowFocus: false, // Don't refetch on tab switch - we have cache
    } as const;
}

export function useGetTransactionsPaginated(fullToken: Token, userId: string, params?: TransactionsPaginatedQueryParams, disabled?: boolean) {
    return useQuery(transactionsPaginatedQuery(fullToken, userId, params, disabled));
}